import { Component, Input, OnInit } from '@angular/core'
import { EmployeeAPIModel, PaginatedAPI } from '../../../models/api.model'
import { debounceTime, filter, map, Observable, Subscription, tap } from 'rxjs'
import { Column, FieldType } from '../../../models/model'
import { FormControl, FormGroup } from '@angular/forms'
import { WebService } from '../../../services/api/web.service'
import { CommonService } from 'src/app/services/common/common.service'

export type SearchParams = {
  pageNo: number
  pageLimit: number
  sortBy: string
  sortOrder: string
}

export type FormattingLegend = {
  formatLabel: string
  formatClassName: string
}

export type ContentSelector = {
  label: string
  selectedValue: string
  selectionOptions: { key: string; value: string }[]
  onChange: Function
}

export type AdditionalButton = {
  id: string
  label: string
  route?: string
  actionType?: 'route' | 'download'
  callback?: (type?: string) => void
}

export type QuickFilter = {
  id: string
  label: string
  criteria: {
    formControlName: string
    filterValue: any
  }[]
}

export type SummaryDataTableData<T> = {
  headers: Column[]
  content$: Observable<PaginatedAPI<T>>
  formattingLegend?: FormattingLegend[]
  contentSelector?: ContentSelector
  additionalButtons: AdditionalButton[]
  currentSearchParams: SearchParams
  checkBoxSelectionIds?: string[]
  cssClasses?: string
  rowFormatting?: Function
  sortCallback?: Function
  searchCallback?: Function
  downloadDocumentCallback?: Function
  selectPageCallback?: Function
  onDropdownLimitChange?: Function
  appliedFilters?: any
  quickFilters?: QuickFilter[]
}

export type SummaryDataTableOptions = {
  downloadable: boolean
  additionalButtons: boolean
  pageable: boolean
  filterable: boolean
  sortable: boolean
  contentSelector: boolean
  contentSelectable: boolean
  formattedContent: boolean
  quickFilters?: boolean
  actionsColumn?: ActionsColumn
}

export type ActionsColumn = {
  actions: Action[] // Array of actions to be displayed in the column
  cssClass?: string // Optional CSS class for styling
}

export type Action = {
  name: string // Button label
  icon?: string // Optional icon class (e.g., FontAwesome or Material)
  cssClass?: string // CSS class for button styling
  callback: (data: any) => void // Function to be executed when action is clicked
  showCondition: (data: any) => boolean //Condition to display the drop-down menu option
}

//TODO: Fix issue: 2-4 API's are getting called for every action on the table
@Component({
  selector: 'app-summary-data-table',
  templateUrl: './summary-data-table.component.html',
  styleUrls: ['./summary-data-table.component.scss']
})
export class SummaryDataTableComponent<T> implements OnInit {
  @Input() options: SummaryDataTableOptions
  @Input() data: SummaryDataTableData<T>

  summarySearchForm: FormGroup
  selectedLimitDropdownValue: number
  public static readonly defaultLimitDropdownValue = 25
  readonly limitDropdownValues = [10, 25, 50, 100]

  employees: EmployeeAPIModel[] = []
  employeesSubscription$: Subscription
  employeeControlSubscription$: Subscription

  autoCompleteHeader: Column
  multiSelectTagHeaders: Column[]
  autoCompleteHeaderFormControl: FormControl
  private mouseSelectedOnAutoComplete: boolean
  dropdownOpen = false

  resetButton = {
    id: 'resetButton',
    label: 'Reset',
    criteria: [
      {
        formControlName: 'reset',
        filterValue: ''
      }
    ]
  }

  constructor(private webService: WebService, private cs: CommonService) {}

  //TODO: Refactor ngOnInit to smaller functions
  ngOnInit(): void {
    document.addEventListener('click', this.closeDropdown.bind(this))

    this.selectedLimitDropdownValue = SummaryDataTableComponent.defaultLimitDropdownValue
    let controls = {}
    this.data.headers.forEach(header => {
      if (header.filter) {
        const value = this.data?.appliedFilters?.[header.filter.key]
          ? this.data?.appliedFilters?.[header.filter.key]
          : ''
        const validators = header?.filter?.validations?.map(_ => _.validator)
        controls[header.filter.key] = new FormControl(value, validators)
        if (header.filter?.showIncludeAndExclude) {
          controls[header.filter.key + '.isInclude'] = new FormControl('true')
        }
      }
    })
    this.summarySearchForm = new FormGroup<any>(controls)

    this.autoCompleteHeader = this.data.headers.find(header => {
      return header.filter && header.filter.type === FieldType.TEXT_AUTOCOMPLETE
    })

    if (this.autoCompleteHeader) {
      this.autoCompleteHeaderFormControl = this.summarySearchForm.get(
        this.autoCompleteHeader.filter?.key
      ) as FormControl

      if (this.autoCompleteHeaderFormControl) {
        this.employeeControlSubscription$ = this.autoCompleteHeaderFormControl.valueChanges
          .pipe(
            debounceTime(500),
            map((value: string) => value?.trim()),
            filter((value: string) => value?.length >= 3),
            tap((value: string) => this._filter(value))
          )
          .subscribe()
      }
    }

    this.multiSelectTagHeaders = this.data.headers.filter(header => {
      return header.filter && header.filter.type === FieldType.MULTISELECT
    })
  }

  searchFormSubmit = () => {
    if (this.summarySearchForm.invalid) {
      this.summarySearchForm.markAllAsTouched()
      return this.cs.showError('Some of the entered filters have incorrect input')
    }
    const updatedFormValue = { ...this.summarySearchForm.value }

    if (this.multiSelectTagHeaders) {
      this.multiSelectTagHeaders.forEach(header => {
        const frmCtrlName = header.filter.key
        // Update the form value for multi-select tag headers
        if (updatedFormValue.hasOwnProperty(frmCtrlName)) {
          const multiSelectTagFrmCtrlValue = updatedFormValue[frmCtrlName]
          updatedFormValue[frmCtrlName] = multiSelectTagFrmCtrlValue
            ? this.getUniqueValuesAsArray(multiSelectTagFrmCtrlValue)
            : ''
        }
      })
    }
    this.data.searchCallback(updatedFormValue)
  }

  onCheckBoxChecked = ($event, record) => {
    if ($event.target.checked) {
      this.data.checkBoxSelectionIds.push(record.id)
    } else {
      let index = this.data.checkBoxSelectionIds.indexOf(record.id)
      this.data.checkBoxSelectionIds.splice(index, 1)
    }
  }

  toggleSelectAll = ($event, records) => {
    if ($event.target.checked) {
      records.forEach(record => {
        if (this.data.checkBoxSelectionIds.indexOf(record.id) < 0) {
          this.data.checkBoxSelectionIds.push(record.id)
        }
      })
    } else {
      this.data.checkBoxSelectionIds = []
    }
  }
  protected readonly FieldType = FieldType

  onLimitValueChange(limit: string) {
    this.data.onDropdownLimitChange(Number(limit))
  }

  displayFn(value?: string) {
    return value && this.employees ? this.employees.filter(_ => _.id === value)[0].employeeCode : undefined
  }

  callGetEmployeeListAPI(employeeCode: string) {
    this.employees = []
    this.employeesSubscription$ = this.webService
      .post('/employee/list?pageNo=0&limit=1000000', { employeeCode })
      .pipe(map((data: PaginatedAPI<EmployeeAPIModel>) => data))
      .subscribe(data => {
        this.employees = data?.content
      })
  }

  private _filter(value: string) {
    const filterValue = this._normalizeValue(value)
    this.callGetEmployeeListAPI(filterValue)
  }

  private _normalizeValue(value: string): string {
    return value.toLowerCase().replace(/\s/g, '')
  }

  onSelect(value: any, frmCtrlName: string) {
    this.summarySearchForm?.get(frmCtrlName)?.setValue(value !== undefined && value !== null ? value : '')
    if (this.mouseSelectedOnAutoComplete) {
      this.searchFormSubmit()
      this.mouseSelectedOnAutoComplete = false
    }
  }

  onOptionMouseDown(): void {
    this.mouseSelectedOnAutoComplete = true
  }

  validateDateInput(event, header: Column) {
    if (!event.target.value) {
      return
    }
    const inputDate = new Date(event.target.value)
    if (isNaN(inputDate.getTime())) {
      if (header?.filter?.key) {
        this.summarySearchForm.get(header.filter.key).setValue(inputDate)
      } else {
        this.cs.showError('Invalid date entered')
      }
    }
  }

  createValidationErrorMessages(header: Column) {
    const errorMessages = {}
    if (header?.filter?.validations) {
      for (const validation of header.filter.validations) {
        errorMessages[validation.validationType] = validation.message
      }
    }
    return errorMessages
  }

  getUniqueValuesAsArray(tagsObj: { value: string; display: string }[]): string[] {
    const values = tagsObj?.map(item => item.value)
    return [...new Set(values)]
  }

  transformOptionList(optionList: Map<string, string>): { value: string; display: string }[] {
    if (!optionList) return []
    return Array.from(optionList, ([key, value]) => ({ value: key, display: value }))
  }

  handleFilter(button: { criteria: { formControlName: string; filterValue: string }[] }) {
    const resetCriteria = button?.criteria.find(criterion => criterion.formControlName === 'reset')

    if (resetCriteria) {
      this.summarySearchForm.reset()
      //TODO: refactor reset implementation better create seperate callback for reset (this.data.reset) or send object {search: , button: ,reset: true}
      this.data.searchCallback(this.summarySearchForm.value, null, true)
    } else {
      button?.criteria.forEach(({ formControlName, filterValue }) => {
        if (formControlName) {
          this.summarySearchForm.get(formControlName)?.setValue(filterValue)
        }
      })
      this.data.searchCallback(this.summarySearchForm.value, button)
    }
  }

  toggleDropdown(event: Event) {
    event.stopPropagation()
    this.dropdownOpen = !this.dropdownOpen
  }

  closeDropdown() {
    this.dropdownOpen = false
  }

  ngOnDestroy() {
    document.removeEventListener('click', this.closeDropdown.bind(this))
  }
}
